/*
 * Nuvoro header behaviour.
 * Custom elements: <sticky-header>, <static-header>, <header-drawer>, <header-menu>,
 * <products-mega-menu>, <header-search>.
 * The cart icon itself is wired up by the cart drawer / notification scripts.
 */
(() => {
  const root = document.documentElement;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const lockFocus = (container, target) => {
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(container, target);
    } else if (target && typeof target.focus === 'function') {
      target.focus();
    }
  };

  const releaseFocus = (target) => {
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(target);
    } else if (target && typeof target.focus === 'function') {
      target.focus();
    }
  };

  const isEscape = (event) => event.key === 'Escape' || event.code === 'Escape';

  const headerSection = (el) => el.closest('.section-header') || el.closest('.shopify-section') || el.parentElement;

  /* ---------------------------------------------------------------- *
   * Header wrappers: keep --header-height in sync, handle sticky modes
   * ---------------------------------------------------------------- */
  class HeaderShell extends HTMLElement {
    connectedCallback() {
      this.section = headerSection(this);
      this.writeHeight = () => {
        if (!this.section) return;
        root.style.setProperty('--header-height', `${this.section.offsetHeight}px`);
      };
      this.writeHeight();
      if ('ResizeObserver' in window) {
        this.sizeWatcher = new ResizeObserver(() => this.writeHeight());
        this.sizeWatcher.observe(this.section);
      } else {
        window.addEventListener('resize', this.writeHeight);
      }
    }

    disconnectedCallback() {
      if (this.sizeWatcher) this.sizeWatcher.disconnect();
      window.removeEventListener('resize', this.writeHeight);
    }
  }

  class StaticHeader extends HeaderShell {}

  class StickyHeader extends HeaderShell {
    connectedCallback() {
      super.connectedCallback();
      if (!this.section) return;

      const mode = this.dataset.stickyType || 'on-scroll-up';
      this.pinned = mode === 'always' || mode === 'reduce-logo-size';
      this.lastScroll = 0;
      this.preventReveal = false;
      this.preventHide = false;
      this.revealTimer = null;

      // Where the header sits in the page flow. Only re-read while it is not stuck,
      // because a stuck (or hidden) header reports its viewport position instead.
      this.measure = () => {
        const offset = window.pageYOffset || root.scrollTop;
        if (this.bounds && offset > this.bounds.top) {
          this.bounds.bottom = this.bounds.top + this.section.offsetHeight;
          return;
        }
        const top = this.section.getBoundingClientRect().top + offset;
        this.bounds = { top, bottom: top + this.section.offsetHeight };
      };
      this.measure();

      if (this.pinned) this.section.classList.add('shopify-section-header-sticky');

      this.handleScroll = this.onScroll.bind(this);
      this.handleResize = () => this.measure();
      this.handlePreventReveal = () => {
        this.preventReveal = true;
      };

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);
      this.addEventListener('preventHeaderReveal', this.handlePreventReveal);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      this.removeEventListener('preventHeaderReveal', this.handlePreventReveal);
      window.clearTimeout(this.revealTimer);
    }

    onScroll() {
      const position = window.pageYOffset || root.scrollTop;
      const search = this.querySelector('predictive-search');
      if (search && search.isOpen) return;

      const pastHeader = position > this.bounds.bottom;
      const goingDown = position > this.lastScroll;

      if (pastHeader && goingDown) {
        this.section.classList.add('scrolled-past-header');
        if (!this.preventHide) window.requestAnimationFrame(() => this.hide());
      } else if (pastHeader && position < this.lastScroll) {
        this.section.classList.add('scrolled-past-header');
        if (this.preventReveal) {
          window.clearTimeout(this.revealTimer);
          this.revealTimer = window.setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          window.requestAnimationFrame(() => this.hide());
        } else {
          window.requestAnimationFrame(() => this.reveal());
        }
      } else if (position <= this.bounds.top) {
        this.section.classList.remove('scrolled-past-header');
        window.requestAnimationFrame(() => this.reset());
      }

      this.lastScroll = position;
    }

    hide() {
      if (this.pinned) return;
      this.section.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeOpenMenus();
      this.closeSearch();
    }

    reveal() {
      if (this.pinned) return;
      this.section.classList.add('shopify-section-header-sticky', 'animate');
      this.section.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.pinned) return;
      this.section.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeOpenMenus() {
      this.querySelectorAll('header-menu, products-mega-menu').forEach((menu) => {
        if (typeof menu.close === 'function') menu.close();
      });
    }

    closeSearch() {
      this.querySelectorAll('header-search').forEach((modal) => {
        if (typeof modal.close === 'function' && modal.isOpen()) modal.close(false);
      });
    }
  }

  /* ---------------------------------------------------------------- *
   * Dropdown / mega menu disclosure
   * ---------------------------------------------------------------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      this.wrapper = this.closest('.header-wrapper');

      this.handleToggle = this.onToggle.bind(this);
      this.handleFocusOut = () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      };
      this.handleKey = (event) => {
        if (!isEscape(event) || !this.details.open) return;
        this.close();
        this.summary.focus();
      };

      this.details.addEventListener('toggle', this.handleToggle);
      this.details.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('keyup', this.handleKey);
    }

    disconnectedCallback() {
      if (!this.details) return;
      this.details.removeEventListener('toggle', this.handleToggle);
      this.details.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('keyup', this.handleKey);
    }

    onToggle() {
      const open = this.details.open;
      this.summary.setAttribute('aria-expanded', String(open));
      if (this.wrapper) {
        this.wrapper.preventHide = open;
        if (open && root.style.getPropertyValue('--header-bottom-position-desktop') === '') {
          root.style.setProperty('--header-bottom-position-desktop', `${Math.floor(this.wrapper.getBoundingClientRect().bottom)}px`);
        }
      }
    }

    close() {
      if (!this.details) return;
      this.details.removeAttribute('open');
      this.summary.setAttribute('aria-expanded', 'false');
    }
  }

  /* ---------------------------------------------------------------- *
   * Products & collections mega menu (opens on hover)
   * ---------------------------------------------------------------- */
  class ProductsMegaMenu extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      this.body = this.querySelector('.products-mega-menu__body');
      this.overlay = this.querySelector('.products-mega-menu__overlay');
      this.items = Array.from(this.querySelectorAll('.products-mega-menu__item'));
      this.wrapper = this.closest('.header-wrapper');

      this.handleEnter = () => this.open();
      this.handleLeave = () => this.close();
      this.handleOutside = (event) => {
        if (!this.details.open) return;
        if (this.body && this.body.contains(event.target) && event.target !== this.overlay) return;
        if (this.summary.contains(event.target)) return;
        this.close();
      };
      this.handleItem = (event) => {
        const item = event.target.closest('.products-mega-menu__item');
        if (item) this.activate(item);
      };
      this.handleToggle = () => {
        this.summary.setAttribute('aria-expanded', String(this.details.open));
        if (this.wrapper) this.wrapper.preventHide = this.details.open;
      };
      this.handleKey = (event) => {
        if (!isEscape(event) || !this.details.open) return;
        this.close();
        this.summary.focus();
      };
      this.handleFocusOut = () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      };

      this.summary.addEventListener('mouseenter', this.handleEnter);
      if (this.overlay) {
        this.overlay.addEventListener('mouseenter', this.handleLeave);
        this.overlay.addEventListener('click', this.handleLeave);
      }
      this.items.forEach((item) => {
        item.addEventListener('mouseover', this.handleItem);
        item.addEventListener('focusin', this.handleItem);
      });
      this.details.addEventListener('toggle', this.handleToggle);
      this.details.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('keyup', this.handleKey);
      document.addEventListener('click', this.handleOutside);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutside);
    }

    activate(item) {
      this.items.forEach((entry) => entry.classList.toggle('products-mega-menu__item--active', entry === item));
    }

    open() {
      const header = this.closest('header') || document;
      header.querySelectorAll('header-menu, products-mega-menu').forEach((menu) => {
        if (menu !== this && typeof menu.close === 'function') menu.close();
      });
      this.details.setAttribute('open', '');
    }

    close() {
      if (!this.details) return;
      this.details.removeAttribute('open');
    }
  }

  /* ---------------------------------------------------------------- *
   * Off-canvas drawer with sliding sub menus
   * ---------------------------------------------------------------- */
  class HeaderDrawer extends HTMLElement {
    connectedCallback() {
      this.container = this.querySelector('details');
      if (!this.container) return;
      this.toggle = this.container.querySelector(':scope > summary');

      this.handleSummary = this.onSummaryClick.bind(this);
      this.handleBack = (event) => this.closeSubmenu(event.currentTarget.closest('details'));
      this.handleClose = (event) => {
        event.preventDefault();
        this.closeDrawer(this.toggle);
      };
      this.handleKey = this.onKeyUp.bind(this);
      this.handleFocusOut = () => {
        window.setTimeout(() => {
          if (this.container.hasAttribute('open') && !this.container.contains(document.activeElement)) this.closeDrawer();
        });
      };

      this.querySelectorAll('summary').forEach((summary) => summary.addEventListener('click', this.handleSummary));
      this.querySelectorAll('.menu-drawer__close-button').forEach((button) => button.addEventListener('click', this.handleBack));
      this.querySelectorAll('.menu-drawer__close-menu-btn').forEach((button) => button.addEventListener('click', this.handleClose));
      this.addEventListener('keyup', this.handleKey);
      this.addEventListener('focusout', this.handleFocusOut);
    }

    disconnectedCallback() {
      if (!this.container) return;
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    onKeyUp(event) {
      if (!isEscape(event)) return;
      const openDetails = event.target.closest('details[open]');
      if (!openDetails) return;
      if (openDetails === this.container) {
        this.closeDrawer(this.toggle);
      } else {
        this.closeSubmenu(openDetails);
      }
    }

    onSummaryClick(event) {
      const summary = event.currentTarget;
      const details = summary.parentNode;

      if (details === this.container) {
        const isOpen = details.hasAttribute('open');
        if (isOpen) event.preventDefault();
        if (isOpen) {
          this.closeDrawer(summary);
        } else {
          this.openDrawer(summary);
        }
        root.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        return;
      }

      const panel = summary.nextElementSibling;
      const parentPanel = details.closest('.has-submenu');
      window.setTimeout(() => {
        details.classList.add('menu-opening');
        summary.setAttribute('aria-expanded', 'true');
        if (parentPanel) parentPanel.classList.add('submenu-open');
        let focused = false;
        const focusFirst = () => {
          if (focused || !details.hasAttribute('open')) return;
          focused = true;
          lockFocus(panel, details.querySelector('button'));
        };
        if (reducedMotion() || !panel) {
          focusFirst();
        } else {
          // transitionend can be skipped (hidden tab, interrupted transition), so keep a fallback.
          panel.addEventListener('transitionend', focusFirst, { once: true });
          window.setTimeout(focusFirst, 350);
        }
      }, 100);
    }

    openDrawer(summary) {
      const section = headerSection(this);
      const wrapper = this.closest('.header-wrapper');
      const border = wrapper && wrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
      if (section) {
        root.style.setProperty('--header-bottom-position', `${parseInt(section.getBoundingClientRect().bottom - border, 10)}px`);
        section.classList.add('menu-open');
      }
      window.setTimeout(() => this.container.classList.add('menu-opening'));
      summary.setAttribute('aria-expanded', 'true');
      lockFocus(this.container, summary);
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeDrawer(focusTarget = null) {
      if (!this.container.hasAttribute('open')) return;
      this.container.classList.remove('menu-opening');
      this.container.querySelectorAll('details').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
        const summary = details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      });
      this.container.querySelectorAll('.submenu-open').forEach((panel) => panel.classList.remove('submenu-open'));
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      const section = headerSection(this);
      if (section) section.classList.remove('menu-open');
      releaseFocus(focusTarget);
      this.finishClosing(this.container);
    }

    closeSubmenu(details) {
      if (!details) return;
      const parentPanel = details.closest('.submenu-open');
      if (parentPanel) parentPanel.classList.remove('submenu-open');
      details.classList.remove('menu-opening');
      const summary = details.querySelector('summary');
      summary.setAttribute('aria-expanded', 'false');
      releaseFocus(summary);
      this.finishClosing(details);
    }

    finishClosing(details) {
      const delay = reducedMotion() ? 0 : 400;
      window.setTimeout(() => {
        if (details.classList.contains('menu-opening')) return;
        details.removeAttribute('open');
        const parent = details.parentElement && details.parentElement.closest('details[open]');
        if (parent) lockFocus(parent, details.querySelector('summary'));
      }, delay);
    }
  }

  /* ---------------------------------------------------------------- *
   * Search modal
   * ---------------------------------------------------------------- */
  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      this.summary.setAttribute('role', 'button');
      this.summary.setAttribute('aria-expanded', 'false');

      this.handleSummary = (event) => {
        event.preventDefault();
        if (this.isOpen()) {
          this.close();
        } else {
          this.open();
        }
      };
      this.handleKey = (event) => {
        if (isEscape(event) && this.isOpen()) this.close();
      };
      this.handleCloseButton = () => this.close();
      this.handleOutside = (event) => {
        if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
      };

      this.summary.addEventListener('click', this.handleSummary);
      this.details.addEventListener('keyup', this.handleKey);
      this.querySelectorAll('.search-modal__close-button').forEach((button) => button.addEventListener('click', this.handleCloseButton));
    }

    disconnectedCallback() {
      if (this.isOpen()) document.body.classList.remove('overflow-hidden');
      document.body.removeEventListener('click', this.handleOutside);
    }

    isOpen() {
      return Boolean(this.details && this.details.hasAttribute('open'));
    }

    open() {
      this.details.setAttribute('open', '');
      this.summary.setAttribute('aria-expanded', 'true');
      document.body.addEventListener('click', this.handleOutside);
      document.body.classList.add('overflow-hidden');
      const panel = this.details.querySelector('[tabindex="-1"]');
      const input = this.details.querySelector('input:not([type="hidden"])');
      lockFocus(panel, input);
      if (input) {
        const length = input.value.length;
        try {
          input.setSelectionRange(length, length);
        } catch (error) {
          /* search inputs in some browsers refuse selection ranges */
        }
      }
    }

    close(returnFocus = true) {
      if (!this.details) return;
      releaseFocus(returnFocus ? this.summary : null);
      this.details.removeAttribute('open');
      this.summary.setAttribute('aria-expanded', 'false');
      document.body.removeEventListener('click', this.handleOutside);
      document.body.classList.remove('overflow-hidden');
    }
  }

  const register = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  register('static-header', StaticHeader);
  register('sticky-header', StickyHeader);
  register('header-menu', HeaderMenu);
  register('products-mega-menu', ProductsMegaMenu);
  register('header-drawer', HeaderDrawer);
  register('header-search', HeaderSearch);
})();
